import { useState, useRef, type ReactNode } from "react";
import styled from "@emotion/styled";
import { opacity } from "@atlas-int/tokens";
import { TooltipAbove } from "./Tooltip";

interface DisabledOverlayProps {
  disabled: boolean;
  /** Message shown in the default tooltip. Ignored when `renderTooltip` is provided. */
  reason?: string;
  children: ReactNode;
  /**
   * When false, skips the opacity/pointer-events dimming.
   * Useful when the caller already applies disabled styling (e.g. a CSS class).
   * @default true
   */
  dimChildren?: boolean;
  /**
   * Custom tooltip renderer. Receives a ref to the container and the hover state
   * so callers can position the tooltip however they need.
   */
  renderTooltip?: (containerRef: React.RefObject<HTMLDivElement | null>) => ReactNode;
}

const Container = styled.div`
  position: relative;
`;

const DisabledChildren = styled.div`
  opacity: ${opacity.muted};
  cursor: not-allowed;

  & > * {
    pointer-events: none;
  }
`;

/**
 * Generic wrapper that dims children and shows a tooltip on hover when disabled.
 *
 * When `disabled` is false the children render normally with no extra wrapper DOM.
 */
const DisabledOverlay = ({
  disabled,
  reason = "This feature is not available",
  children,
  dimChildren = true,
  renderTooltip,
}: DisabledOverlayProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  if (!disabled) {
    return <>{children}</>;
  }

  const tooltip = isHovered
    ? renderTooltip
      ? renderTooltip(containerRef)
      : <TooltipAbove>{reason}</TooltipAbove>
    : null;

  const content = dimChildren ? (
    <DisabledChildren>{children}</DisabledChildren>
  ) : (
    children
  );

  return (
    <Container
      ref={containerRef}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      {content}
      {tooltip}
    </Container>
  );
};

export default DisabledOverlay;
export type { DisabledOverlayProps };
